import type { AccessEnum, ProjectFund } from '@/store/project-fund';
import type { ColDef, ColGroupDef, ValueFormatterParams } from 'ag-grid-community';
import { CheckboxCell, ColorPicker } from './ui/customInputs';
import dayjs from 'dayjs';
import type { ProjectFundWellInfo, ValidateWellTableName } from '@/api/api.v2';
import { ProjectFundWellInfoWithIndex } from './types';

export const getProjectFundColumns = (
    selectedProjectFonds: number[],
    handleToggleFond: (id: number) => void,
): Array<ColDef<ProjectFund> | ColGroupDef<ProjectFund>> => [
    {
        width: 32,
        maxWidth: 32,
        cellRenderer: CheckboxCell,
        cellRendererParams: {
            onToggleFond: handleToggleFond,
            selectedProjectFonds,
        },
        cellClass: '!flex items-center',
    },
    {
        flex: 1,
        minWidth: 200,
        field: 'title',
        headerName: 'Название',
    },
    {
        width: 110,
        field: 'createUser',
        headerName: 'Автор',
    },
    {
        width: 120,
        field: 'createDate',
        headerName: 'Дата создания',
        valueFormatter: params => (params.value ? dayjs(params.value as string).format('DD.MM.YYYY HH:mm') : ''),
    },
    {
        width: 110,
        field: 'changeUser',
        headerName: 'Редактор',
    },
    {
        width: 110,
        field: 'changeDate',
        headerName: 'Дата изменения',
        valueFormatter: params => (params.value ? dayjs(params.value as string).format('DD.MM.YYYY HH:mm') : ''),
    },
    {
        width: 32,
        maxWidth: 32,
        cellRenderer: ColorPicker,
        cellClass: '!flex items-center',
    },
];

export const ROW_HEIGHT = 32;

export const GRID_OPTIONS = {
    suppressRowTransform: true,
    defaultColDef: {
        resizable: true,
        suppressMovable: true, // запрет на перемещение (dragging) колонок
    },
};

export const SECURITY_LEVELS: Record<AccessEnum, string> = { 1: 'Личный', 2: 'Для чтения', 3: 'Общий' };

export const ACCESS_TYPES = Object.entries(SECURITY_LEVELS).map(([value, label]) => ({
    value: Number(value) as AccessEnum,
    label: <span className="text-xs">{label}</span>,
}));

export const VALIDATION_KEYS_MAP: Partial<Record<keyof ProjectFundWellInfo, ValidateWellTableName>> = {
    mineId: 'mine',
    explorationZoneId: 'expZone',
    areaId: 'area',
    oilFieldId: 'stratum',
    kupolId: 'kupol',
};

export const ADDITIONAL_KEYS_MAP: Partial<
    Record<keyof ProjectFundWellInfo, 'explorationZone' | 'mineNumber' | 'stratum' | 'kupol' | 'area'>
> = {
    mineId: 'mineNumber',
    explorationZoneId: 'explorationZone',
    areaId: 'area',
    oilFieldId: 'stratum',
    kupolId: 'kupol',
};

export const WELL_COLUMNS: Array<ColDef<ProjectFundWellInfoWithIndex> | ColGroupDef<ProjectFundWellInfoWithIndex>> = [
    {
        headerName: '№',
        field: 'index',
        valueFormatter: ({ value }: ValueFormatterParams<ProjectFundWellInfoWithIndex, number>) =>
            value != null ? (value + 1).toString() : '',
        width: 60,
    },
    {
        field: 'FieldTitle',
        headerName: 'Месторождение',
        editable: false,
        width: 116,
    },
    {
        field: 'mineId',
        headerName: 'Куст',
        valueGetter: params => params.data?.mineId ?? params.data?.mineNumber,
        cellClass: '!flex items-center',
        width: 90,
    },
    {
        field: 'wellNumber',
        headerName: 'Скважина',
        cellClass: '!flex items-center',
        width: 90,
    },
    {
        field: 'oilFieldId',
        headerName: 'Объект (пласт)',
        valueGetter: params => params.data?.oilFieldId ?? params.data?.stratum,
        cellClass: '!flex items-center',
        width: 100,
    },
    {
        field: 'wellboreValuesId',
        headerName: 'Тип ствола',
        cellClass: '!flex items-center',
        width: 102,
    },
    {
        field: 'licenseZoneId',
        headerName: 'Лицензионный участок',
        cellClass: '!flex items-center',
        width: 145,
    },
    {
        field: 'areaId',
        headerName: 'Площадь',
        valueGetter: params => params.data?.areaId ?? params.data?.area,
        cellClass: '!flex items-center',
        width: 100,
    },
    {
        field: 'developmentIconId',
        headerName: 'Категория',
        cellClass: '!flex items-center',
        width: 140,
    },
    {
        headerName: 'Коор. пластопересечения',
        children: [
            {
                headerName: 'X',
                field: 'stratumIntersectX',
                cellClass: '!flex items-center',
                width: 86,
            },
            {
                headerName: 'Y',
                field: 'stratumIntersectY',
                cellClass: '!flex items-center',
                width: 86,
            },
        ],
    },
    {
        headerName: 'Коор. забоя ГС',
        children: [
            {
                headerName: 'X',
                field: 'bottomHoleX',
                cellClass: '!flex items-center',
                width: 69,
            },
            {
                headerName: 'Y',
                field: 'bottomHoleY',
                cellClass: '!flex items-center',
                width: 69,
            },
        ],
    },
    {
        headerName: 'Коор. устья',
        children: [
            {
                headerName: 'X',
                field: 'outFallX',
                cellClass: '!flex items-center',
                width: 58,
            },
            {
                headerName: 'Y',
                field: 'outFallY',
                cellClass: '!flex items-center',
                width: 58,
            },
        ],
    },
    {
        field: 'drillDate',
        headerName: 'Предполаг. бурение',
        width: 138,
    },
    {
        field: 'createdAt',
        headerName: 'Дата создания',
        width: 138,
    },
];
